'use client';

import { fetchProductList } from '@/api/product';
import { listByProductIds } from '@/api/public';
import { ListProduct } from '@/api/types/public';
import InfiniteScroll from '@/components/InfiniteScroll';
import ProductCard from '@/components/Product/ProductCard';
import YouMightLikeWaterFall from '@/app/cart/components/YouMightLikeWaterFall';
import { ResponsiveContext } from '@/components/Responsive/ResponsiveProvider';
import logger from '@/utils/client-logger';
import { EPlatform } from '@/utils/enums/platform-enum';
import { useWhenTokenReady } from '@/hooks/useWhenReady';
import { useCallback, useContext, useEffect, useRef, useState } from 'react';
import { useInView } from 'react-intersection-observer';
import type { MarketingRuntimeConfig, ProductRecommendConfig } from '../types';
import { useCampaignRecommendTracking } from '../pages/SeedingPage/hooks/useCampaignRecommendTracking';
import { useStore } from '@/providers/app-store-provider';

const DEFAULT_RECOMMEND_TITLE = 'You may also like';

/** 与购物车底部 YouMightLikeWaterFall 保持一致的视觉样式，但不耦合组件实现 */
const RECOMMEND_SECTION_CLASS = {
  root: (isWap: boolean) =>
    `bg-white font-regular w-full ${isWap ? 'mx-auto pt-[24px] px-[8px]' : 'mx-auto pt-[40px] px-[48px]'} pb-[60px]`,
  title: (isWap: boolean) =>
    `font-bb text-center ${isWap ? 'text-[20px] leading-[24px] mb-[16px]' : 'text-[32px] leading-[40px] mb-[32px]'}`,
  grid: (isWap: boolean) =>
    isWap
      ? 'grid grid-cols-2 gap-x-[8px] gap-y-[40px] relative'
      : 'grid gap-x-[16px] gap-y-[24px] 2xl:grid-cols-4 xl:grid-cols-3 lg:grid-cols-2 sm:grid-cols-1 pb-[64px]',
  infiniteScroll: (isWap: boolean) =>
    isWap ? 'pt-[40px]' : 'h-[64px] flex items-center justify-center',
} as const;

interface ProductRecommendProps {
  config: ProductRecommendConfig;
  runtimeConfig: MarketingRuntimeConfig;
  enableAdTracking?: boolean;
  /** 推荐商品全部加载完成（含无数据场景） */
  onLoadComplete?: () => void;
}

/** 是否配置了可展示的推荐商品版位 */
export function isProductRecommendEnabled(config?: ProductRecommendConfig): boolean {
  if (!config?.source_type) return false;
  if (config.source_type === 'algorithm') return true;
  if (config.source_type === 'group') return Number(config.group_id) > 0;
  return false;
}

/**
 * 商品推荐版位组件
 * 支持 source_type='group'（指定分组）和 source_type='algorithm'（算法推荐）两种模式
 */
export default function ProductRecommend({
  config,
  runtimeConfig,
  enableAdTracking = false,
  onLoadComplete,
}: ProductRecommendProps) {
  const { deviceType } = useContext(ResponsiveContext);

  if (config.source_type === 'algorithm') {
    return (
      <AlgorithmRecommend
        runtimeConfig={runtimeConfig}
        deviceType={deviceType}
        title={config.section_title}
        enableAdTracking={enableAdTracking}
        onLoadComplete={onLoadComplete}
      />
    );
  }

  if (config.source_type === 'group' && config.group_id) {
    return (
      <GroupRecommend
        runtimeConfig={runtimeConfig}
        groupId={config.group_id}
        deviceType={deviceType}
        title={config.section_title}
        enableAdTracking={enableAdTracking}
        onLoadComplete={onLoadComplete}
      />
    );
  }

  return null;
}

interface GroupRecommendProps {
  runtimeConfig: MarketingRuntimeConfig;
  groupId: number;
  deviceType: number;
  title?: string;
  enableAdTracking?: boolean;
  onLoadComplete?: () => void;
}

function GroupRecommend({
  runtimeConfig,
  groupId,
  deviceType,
  title,
  enableAdTracking = false,
  onLoadComplete,
}: GroupRecommendProps) {
  const isWap = deviceType !== EPlatform.WEB;
  const sectionTitle = title || DEFAULT_RECOMMEND_TITLE;
  const [list, setList] = useState<ListProduct[]>([]);
  const [page, setPage] = useState(0);
  const [loading, setLoading] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [total, setTotal] = useState(0);
  const loadingRef = useRef(false);
  const latestRequestIdRef = useRef(0);
  const loadCompleteNotifiedRef = useRef(false);
  const modelExpSentRef = useRef(false);
  const prevTokenRef = useRef<string | undefined>(undefined);
  const token = useStore((state) => state.token);

  const {
    trackRecommendClick,
    trackRecommendExp,
    trackRecommendAdExposure,
    trackRecommendAdClick,
    trackRecommendCollectClick,
    trackRecommendValidExp,
    trackRecommendModelExp,
    collectScene,
  } = useCampaignRecommendTracking(runtimeConfig);

  const { ref: titleRef, inView: titleInView } = useInView({
    threshold: 0.5,
    triggerOnce: true,
  });

  const notifyLoadComplete = useCallback(() => {
    if (loadCompleteNotifiedRef.current) return;
    loadCompleteNotifiedRef.current = true;
    onLoadComplete?.();
  }, [onLoadComplete]);

  const loadPage = useCallback(
    async (nextPage: number, append: boolean) => {
      if (loadingRef.current) return;

      const requestId = latestRequestIdRef.current + 1;
      latestRequestIdRef.current = requestId;
      const isLatestRequest = () => latestRequestIdRef.current === requestId;

      loadingRef.current = true;
      setLoading(true);

      try {
        const res = await fetchProductList({
          group_id: groupId,
          page: nextPage,
          limit: 20,
          append_correlation: 1,
        });
        if (!isLatestRequest()) return;

        if (res.code === 200 && res.data) {
          const newList = res.data.list || [];
          setList((prev) => {
            const merged = append ? [...prev, ...newList] : newList;
            if (newList.length < 20 || (res.data?.total && merged.length >= res.data.total)) {
              setHasMore(false);
            } else {
              setHasMore(true);
            }
            return merged;
          });
          setPage(nextPage);
          setTotal(res.data.total || 0);
        } else {
          if (!append) {
            setList([]);
            setPage(0);
            setTotal(0);
          }
          setHasMore(false);
        }
      } catch (error) {
        if (isLatestRequest()) {
          logger.error(error, 'Failed to load group products:');
          if (!append) {
            setList([]);
            setPage(0);
            setTotal(0);
          }
          setHasMore(false);
        }
      } finally {
        if (isLatestRequest()) {
          loadingRef.current = false;
          setLoading(false);
        }
      }
    },
    [groupId],
  );

  const loadMore = useCallback(async () => {
    if (loadingRef.current || !hasMore) return;
    await loadPage(page + 1, true);
  }, [hasMore, loadPage, page]);

  const updateProductsWishStatus = useCallback(async () => {
    try {
      if (list.length === 0) return;

      const productIds = list.map((item) => item.id || 0).filter((id) => id > 0);
      if (productIds.length === 0) return;

      const response = await listByProductIds({ product_ids: productIds.join(',') });
      if (response?.code !== 200 || !Array.isArray(response.data?.list)) return;

      const wishMap = response.data.list.reduce<Record<number, number>>((acc, item) => {
        if (item.id && typeof item.is_wish === 'number') {
          acc[item.id] = item.is_wish;
        }
        return acc;
      }, {});

      setList((prevList) =>
        prevList.map((item) => ({
          ...item,
          is_wish: item.id && wishMap[item.id] !== undefined ? wishMap[item.id] : item.is_wish,
        })),
      );
    } catch (error) {
      logger.error(error, 'Failed to update campaign recommend wish status:');
    }
  }, [list]);

  useWhenTokenReady(() => {
    if (prevTokenRef.current === undefined) {
      prevTokenRef.current = token;
      return;
    }

    const prevToken = prevTokenRef.current;

    if (prevToken && !token) {
      setList((prevList) =>
        prevList.map((item) => ({
          ...item,
          is_wish: 2,
        })),
      );
    }

    if (token && prevToken !== token) {
      void updateProductsWishStatus();
    }

    prevTokenRef.current = token;
  }, [token, updateProductsWishStatus]);

  useEffect(() => {
    void loadPage(1, false);
  }, [loadPage]);

  useEffect(() => {
    if (!loading && !hasMore) {
      notifyLoadComplete();
    }
  }, [loading, hasMore, notifyLoadComplete]);

  useEffect(() => {
    if (titleInView && list.length > 0 && !modelExpSentRef.current) {
      modelExpSentRef.current = true;
      trackRecommendModelExp();
    }
  }, [titleInView, list.length, trackRecommendModelExp]);

  const renderItem = useCallback(
    (item: ListProduct, index: number) => (
      <div key={`${item.shop_sku || item.id}-${index}`} className="bg-none">
        <ProductCard
          product={{ ...item, name: item.name || item.short_name || item.shop_sku || '' }}
          index={index}
          showTryOn={false}
          handelClick={() => {
            trackRecommendClick(item, index);
            if (enableAdTracking) {
              trackRecommendAdClick(item, index);
            }
          }}
          onInView={() => {
            trackRecommendExp(item, index);
            if (enableAdTracking) {
              trackRecommendAdExposure(item, index);
            }
          }}
          onValidExposure={() => trackRecommendValidExp(item, index)}
          wished={() => trackRecommendCollectClick(item, index, '收藏')}
          removed={() => trackRecommendCollectClick(item, index, '取消收藏')}
          collectScene={collectScene}
          className={isWap ? 'bg-none' : 'h-full bg-none'}
          imageClassName={isWap ? 'h-[176px] rounded-[12px]' : undefined}
          showCorrelation
        />
      </div>
    ),
    [
      isWap,
      enableAdTracking,
      trackRecommendClick,
      trackRecommendExp,
      trackRecommendAdExposure,
      trackRecommendAdClick,
      trackRecommendCollectClick,
      trackRecommendValidExp,
      collectScene,
    ],
  );

  if (list.length === 0 && !loading) {
    return null;
  }

  return (
    <div className={RECOMMEND_SECTION_CLASS.root(isWap)}>
      <div ref={titleRef} className={RECOMMEND_SECTION_CLASS.title(isWap)}>
        {sectionTitle}
      </div>

      <div className={RECOMMEND_SECTION_CLASS.grid(isWap)}>
        {list.map((item, index) => renderItem(item, index))}
      </div>

      {hasMore && (
        <InfiniteScroll
          loading={loading}
          onReachBottom={loadMore}
          page={page}
          total={total}
          limit={20}
          className={RECOMMEND_SECTION_CLASS.infiniteScroll(isWap)}
        />
      )}
    </div>
  );
}

interface AlgorithmRecommendProps {
  runtimeConfig: MarketingRuntimeConfig;
  deviceType: number;
  title?: string;
  enableAdTracking?: boolean;
  onLoadComplete?: () => void;
}

function AlgorithmRecommend({
  runtimeConfig,
  deviceType,
  title,
  enableAdTracking = false,
  onLoadComplete,
}: AlgorithmRecommendProps) {
  const token = useStore((state) => state.token);
  const {
    trackRecommendClick,
    trackRecommendExp,
    trackRecommendAdExposure,
    trackRecommendAdClick,
    trackRecommendCollectClick,
    trackRecommendValidExp,
    trackRecommendModelExp,
    collectScene,
  } = useCampaignRecommendTracking(runtimeConfig);

  const handleProductInView = useCallback(
    (item: Parameters<typeof trackRecommendExp>[0], index: number) => {
      trackRecommendExp(item, index);
      if (enableAdTracking) {
        trackRecommendAdExposure(item, index);
      }
    },
    [enableAdTracking, trackRecommendAdExposure, trackRecommendExp],
  );

  const handleProductClick = useCallback(
    (item: Parameters<typeof trackRecommendClick>[0], index: number) => {
      trackRecommendClick(item, index);
      if (enableAdTracking) {
        trackRecommendAdClick(item, index);
      }
    },
    [enableAdTracking, trackRecommendAdClick, trackRecommendClick],
  );

  return (
    <YouMightLikeWaterFall
      key={`campaign-recommend-${deviceType}-${token}`}
      recommendList={[]}
      deviceType={deviceType as EPlatform}
      sectionTitle={title || DEFAULT_RECOMMEND_TITLE}
      onAllLoaded={onLoadComplete}
      tracking={{
        onItemClick: handleProductClick,
        onProductInView: handleProductInView,
        onProductValidExposure: trackRecommendValidExp,
        onCollectClick: trackRecommendCollectClick,
        onModelExp: trackRecommendModelExp,
        collectScene,
      }}
      pcGridClassName={RECOMMEND_SECTION_CLASS.grid(false)}
      syncWishOnTokenChange
    />
  );
}
